/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Feedback Category Badge Component
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React from 'react'
import { Bug, Lightbulb, FileText, Palette, Gauge, HelpCircle } from 'lucide-react'

const categoryConfig = {
  bug: {
    label: 'Bug Report',
    color: 'red',
    icon: Bug,
    bgClass: 'bg-red-100 dark:bg-red-900/30',
    textClass: 'text-red-800 dark:text-red-300',
    borderClass: 'border-red-300 dark:border-red-700'
  },
  feature_request: {
    label: 'Feature Request',
    color: 'blue',
    icon: Lightbulb,
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    textClass: 'text-blue-800 dark:text-blue-300',
    borderClass: 'border-blue-300 dark:border-blue-700'
  },
  content_error: {
    label: 'Content Error',
    color: 'orange',
    icon: FileText,
    bgClass: 'bg-orange-100 dark:bg-orange-900/30',
    textClass: 'text-orange-800 dark:text-orange-300',
    borderClass: 'border-orange-300 dark:border-orange-700'
  },
  ui_ux: {
    label: 'UI/UX',
    color: 'purple',
    icon: Palette,
    bgClass: 'bg-purple-100 dark:bg-purple-900/30',
    textClass: 'text-purple-800 dark:text-purple-300',
    borderClass: 'border-purple-300 dark:border-purple-700'
  },
  performance: {
    label: 'Performance',
    color: 'yellow',
    icon: Gauge,
    bgClass: 'bg-yellow-100 dark:bg-yellow-900/30',
    textClass: 'text-yellow-800 dark:text-yellow-300',
    borderClass: 'border-yellow-300 dark:border-yellow-700'
  },
  other: {
    label: 'Other',
    color: 'gray',
    icon: HelpCircle,
    bgClass: 'bg-gray-100 dark:bg-gray-700',
    textClass: 'text-gray-800 dark:text-gray-300',
    borderClass: 'border-gray-300 dark:border-gray-600'
  }
}

const FeedbackCategoryBadge = ({ category, showIcon = true, size = 'md' }) => {
  const config = categoryConfig[category] || categoryConfig.other

  const Icon = config.icon

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  }

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full font-medium border
        ${config.bgClass} ${config.textClass} ${config.borderClass}
        ${sizeClasses[size]}
      `}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      <span>{config.label}</span>
    </span>
  )
}

export default FeedbackCategoryBadge
